import {
  useEffect,
  useMemo,
  useRef,
  type ComponentType,
  type SVGProps,
  type TouchEvent,
  type UIEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, History, Play } from 'lucide-react';

import {
  BattlesSwordIcon,
  CommunityHeartIcon,
  DecksCardsIcon,
  EventsTrophyIcon,
  LiveBroadcastIcon,
  RankedSwordsIcon,
  RankingsChartIcon,
  SitngoBoltIcon,
} from './HubIcons';

import { useHasLogtoRole } from '@/hooks/useHasLogtoRole';
import { env } from '@/env';
import { useGameSelection } from '@/hooks/useGameSelection';
import { useHaptics } from '@/hooks/useHaptics';
import { featuresForGame } from '@/lib/gameFeatures';
import { cn } from '@/lib/cn';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setHubSection } from '@/store/core.slice';
import { setBannerCompact } from '@/store/settings.slice';
import { BOUNCE, useSlidingIndicator } from '@/hooks/useSlidingIndicator';
import { BannerSideSlot } from '@/components/layout/BannerSlots';
import { RankedPanel } from './panels/RankedPanel';
import { EventsPanel } from './panels/EventsPanel';
import { SitngoPanel } from './panels/SitngoPanel';
import { CommunityPanel } from './panels/CommunityPanel';
import { LeaderboardPanel } from './panels/LeaderboardPanel';
import { BattlesPanel } from './panels/BattlesPanel';
import { DecksPanel } from './panels/DecksPanel';
import { LiveMatchesPanel } from './panels/LiveMatchesPanel';
import { QcpPlayPanel } from './panels/QcpPlayPanel';
import { QcpLeaderboardPanel } from './panels/QcpLeaderboardPanel';
import { QcpMatchesPanel } from './panels/QcpMatchesPanel';
import { QgammonPlayPanel } from './panels/QgammonPlayPanel';
import { QgammonLeaderboardPanel } from './panels/QgammonLeaderboardPanel';
import { QgammonMatchesPanel } from './panels/QgammonMatchesPanel';

type SectionKey =
  | 'ranked'
  | 'events'
  | 'sitngo'
  | 'community'
  | 'rankings'
  | 'decks'
  | 'battles'
  | 'live'
  | 'play'
  | 'matches';

interface SectionDef {
  key: SectionKey;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  Panel: ComponentType;
  /**
   * The panel brings its own scroller (pinned header + scrolling body). The
   * section wrapper then clips instead of scrolling, so the two don't nest —
   * an outer scroller would drag the header the inner one just pinned.
   */
  ownScroll?: boolean;
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * The Hub's section switcher: a row of buttons under the banner, and the
 * selected section's screen right below them — no navigation, the Hub IS the
 * screen.
 *
 * Sections are game-conditional: "Mie battaglie" and "Top Decks" are Clash
 * Royale things and would land on an empty screen under Chess. Each game brings
 * its own row.
 *
 * The button row stays put; only the panel scrolls. Same contract as the
 * Tournaments tabs — the thing you navigate with must never slide away under
 * your thumb.
 */
export function HubSections() {
  const { t } = useTranslation('hub');
  const haptics = useHaptics();
  const { activeGame } = useGameSelection();
  const features = featuresForGame(activeGame);
  const showCr = features.cr && env.featureClashRoyaleEnabled;
  const showQcp = features.chinesePoker && env.featureChinesePokerEnabled;
  // ⚠️ Backgammon vuole il ruolo `beta` OLTRE al flag, e la tile e' meta' della
  // serratura: l'altra meta' sono le rotte in `routes/router.tsx`. Proteggere
  // solo le rotte lascerebbe una tile che porta a un'area vuota — un vicolo
  // cieco, non una protezione; proteggere solo la tile lascerebbe la sezione
  // raggiungibile per deep-link. Il flag da solo non basta perche' e' GLOBALE:
  // un utente col ruolo `user` porta ZERO scope sulla resource QGammon.
  // Forma inline (non <RoleGate>) perche' qui serve un BOOLEANO che entra in una
  // condizione, non un wrapper attorno a un sottoalbero — e' l'uso documentato
  // in `components/common/RoleGate.tsx`.
  const isBeta = useHasLogtoRole('beta');
  const showQgammon = features.backgammon && env.featureBackgammonEnabled && isBeta;

  // NB: il livello/EXP del giocatore NON sta più qui. Stava dentro il bottone
  // "Ranked", che è un elemento di navigazione: un bottone che cambia sezione
  // non è il posto dove leggere uno stato. Ora vive nell'intestazione del
  // pannello Ranked, accanto al rank a cui appartiene.

  const sections: SectionDef[] = useMemo(() => {
    // Chinese Poker is a full-surface game: its three sections REPLACE the
    // cross-game row (Events/Rankings-Laravel/Live would all be empty for the
    // synthetic game id). Other games keep the existing behavior untouched.
    if (showQcp) {
      return [
        {
          key: 'play',
          Icon: Play,
          label: t('sections.play', { defaultValue: 'Play' }),
          Panel: QcpPlayPanel,
          ownScroll: true,
        },
        {
          key: 'rankings',
          Icon: BarChart3,
          label: t('sections.rankings', { defaultValue: 'Rankings' }),
          Panel: QcpLeaderboardPanel,
        },
        {
          key: 'matches',
          Icon: History,
          label: t('sections.matches', { defaultValue: 'Matches' }),
          Panel: QcpMatchesPanel,
          ownScroll: true,
        },
      ];
    }
    // Backgammon (QGammon) is likewise a full-surface game: its three sections
    // REPLACE the cross-game row for the same reason as Chinese Poker above.
    if (showQgammon) {
      return [
        {
          key: 'play',
          Icon: Play,
          label: t('sections.play', { defaultValue: 'Play' }),
          Panel: QgammonPlayPanel,
          ownScroll: true,
        },
        {
          key: 'rankings',
          Icon: BarChart3,
          label: t('sections.rankings', { defaultValue: 'Rankings' }),
          Panel: QgammonLeaderboardPanel,
        },
        {
          key: 'matches',
          Icon: History,
          label: t('sections.matches', { defaultValue: 'Matches' }),
          Panel: QgammonMatchesPanel,
          ownScroll: true,
        },
      ];
    }
    const list: SectionDef[] = [];
    // Row 1: Ranked · Events · Sit&Go · Community.
    // Row 2: Rankings · Decks · Battles · Live.
    // Events / Rankings / Live are cross-game; the rest are CR surfaces (QDuels
    // is Clash-Royale-only) and drop out under another game.
    if (showCr) {
      list.push({
        key: 'ranked',
        Icon: RankedSwordsIcon,
        label: t('sections.ranked', { defaultValue: 'Ranked' }),
        Panel: RankedPanel,
      });
    }
    list.push({
      key: 'events',
      Icon: EventsTrophyIcon,
      label: t('sections.events', { defaultValue: 'Events' }),
      Panel: EventsPanel,
      ownScroll: true,
    });
    if (showCr) {
      list.push(
        {
          key: 'sitngo',
          Icon: SitngoBoltIcon,
          label: t('sections.sitngo', { defaultValue: 'Sit&Go' }),
          Panel: SitngoPanel,
          ownScroll: true,
        },
        {
          key: 'community',
          Icon: CommunityHeartIcon,
          label: t('sections.community', { defaultValue: 'Community' }),
          Panel: CommunityPanel,
          ownScroll: true,
        },
      );
    }
    // 🔴 LA SCHEDA SEGUE IL PANNELLO, e prima non lo faceva. `LeaderboardPanel` si spegne
    // da se' col flag (`LeaderboardPanel.tsx:67`, `return null`) — corretto — ma la scheda
    // veniva offerta comunque: su una build con la classifica spenta l'utente toccava
    // «CLASSIFICA» e otteneva **uno schermo vuoto**. Non un errore, non un vuoto
    // dichiarato: NIENTE.
    //
    // 📌 E' la terza forma dello stesso guasto trovata il 2026-09-05, e la peggiore delle
    // tre: un vuoto affermato (#133) dice una cosa falsa, uno spinner non dice niente ma si
    // vede che sta lavorando — **una schermata vuota non dichiara nemmeno di esistere**, e
    // nessun controllo la intercetta, perche' non parte nessuna chiamata e non fallisce
    // nulla. L'ha trovata `or45` guardando l'`innerText` del body, non la rete.
    //
    // ⇒ La regola: **un pannello che puo' renderizzare `null` non deve avere una scheda.**
    // Chi aggiunge un `return null` in cima a un pannello del Hub gatta ANCHE il push qui,
    // con la stessa condizione. Oggi e' l'unico caso — verificato con un grep su tutti i
    // pannelli — ed e' scritto qui perche' resti l'unico.
    if (env.featureLeaderboardEnabled) {
      list.push({
        key: 'rankings',
        Icon: RankingsChartIcon,
        label: t('sections.rankings', { defaultValue: 'Rankings' }),
        Panel: LeaderboardPanel,
        // Ora porta la propria card-finestra con lo scroller dentro: il
        // contenitore esterno deve CLIPPARE, non scorrere, altrimenti i due
        // scroller si annidano e il gesto finisce su quello sbagliato.
        ownScroll: true,
      });
    }
    if (showCr) {
      list.push(
        {
          key: 'decks',
          Icon: DecksCardsIcon,
          label: t('sections.decks', { defaultValue: 'Decks' }),
          Panel: DecksPanel,
          ownScroll: true,
        },
        {
          key: 'battles',
          Icon: BattlesSwordIcon,
          label: t('sections.battles', { defaultValue: 'Battles' }),
          Panel: BattlesPanel,
          ownScroll: true,
        },
      );
    }
    list.push({
      key: 'live',
      Icon: LiveBroadcastIcon,
      label: t('sections.live', { defaultValue: 'Live' }),
      Panel: LiveMatchesPanel,
    });
    return list;
  }, [t, showCr, showQcp, showQgammon]);

  const dispatch = useAppDispatch();
  const storedSection = useAppSelector((s) => s.core.hubSection) as SectionKey | null;
  // Derive rather than store-and-correct: the section row is game-conditional, so a
  // stored key can point at a section that no longer exists (CR → Chess). Deriving
  // also removes the old setState-in-effect and its one-frame flash of the wrong panel.
  const active: SectionKey =
    (storedSection && sections.some((s) => s.key === storedSection) ? storedSection : sections[0]?.key) ?? 'rankings';

  const selectSection = (key: SectionKey) => {
    if (key === active) return;
    haptics.selection();
    dispatch(setHubSection(key));
  };

  /**
   * Swipe navigates the button GRID, following its layout:
   *  - horizontal → the next/previous button in READING ORDER across the whole
   *    grid (left→right advances one button; past the last button of a row you
   *    land on the first of the next, and past the very last you wrap to the
   *    very first);
   *  - vertical → the same column one row up/down, wrapping round the rows.
   * Directions are literal: a left→right swipe advances one button, an up→down
   * swipe drops a row.
   *
   * The full 2D gesture lives on the button row (nothing scrolls there to
   * fight); the panel takes only the HORIZONTAL half, so a vertical swipe still
   * scrolls the panel's own content. A pointer-position pair is all we need —
   * the delta between touchdown and lift.
   */
  const COLS = 4;
  const {
    containerRef: gridRef,
    rect: gridRect,
    ready: gridReady,
  } = useSlidingIndicator(active);
  // L'elemento che scorre davvero: il contenitore esterno per le sezioni
  // semplici, quello interno per quelle con `ownScroll`. Registrato al primo
  // scroll, così il tasto "torna in cima" agisce su quello giusto.
  const scrollerRef = useRef<HTMLElement | null>(null);
  const touchRef = useRef<{ x: number; y: number } | null>(null);
  // Set when a swipe was handled on the buttons, so the click the browser
  // synthesises on the button under the finger is swallowed, not re-selected.
  const swipeConsumedRef = useRef(false);
  const SWIPE_MIN_PX = 48;

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    swipeConsumedRef.current = false;
    touchRef.current = t ? { x: t.clientX, y: t.clientY } : null;
  };

  const gestureDelta = (e: TouchEvent<HTMLDivElement>) => {
    const start = touchRef.current;
    touchRef.current = null;
    const t = e.changedTouches[0];
    if (!start || !t) return null;
    return { dx: t.clientX - start.x, dy: t.clientY - start.y };
  };

  const moveByGesture = (dx: number, dy: number) => {
    const i = sections.findIndex((s) => s.key === active);
    if (i === -1) return;
    let target: SectionDef | undefined;
    if (Math.abs(dx) >= Math.abs(dy)) {
      // Horizontal: linear through the whole grid in reading order, wrap end↔start.
      const n = sections.length;
      target = sections[dx > 0 ? (i + 1) % n : (i - 1 + n) % n];
    } else {
      // Vertical: same column, one row up/down, wrapping the rows.
      const rows = chunk(sections, COLS);
      const row = Math.floor(i / COLS);
      const col = i % COLS;
      const numRows = rows.length;
      const nextRow = dy > 0 ? (row + 1) % numRows : (row - 1 + numRows) % numRows;
      target = rows[nextRow][Math.min(col, rows[nextRow].length - 1)];
    }
    if (target) selectSection(target.key);
  };

  // Buttons: the whole grid (both axes).
  const onButtonsTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    const d = gestureDelta(e);
    if (!d) return;
    if (Math.max(Math.abs(d.dx), Math.abs(d.dy)) < SWIPE_MIN_PX) return;
    swipeConsumedRef.current = true;
    moveByGesture(d.dx, d.dy);
  };

  // Panel: horizontal only — a vertical swipe stays with the panel's own scroll.
  const onPanelTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    const d = gestureDelta(e);
    if (!d) return;
    if (Math.abs(d.dx) < SWIPE_MIN_PX || Math.abs(d.dx) < Math.abs(d.dy) * 1.5) return;
    moveByGesture(d.dx, 0);
  };

  // La compressione del banner e' guidata da <AppShell />, che ascolta lo
  // scroll di TUTTE le rotte. Qui la leggiamo soltanto: serve a nascondere la
  // griglia e a spostare il tasto della sezione accanto al banner.
  const compact = useAppSelector((s) => s.settings.bannerCompact);

  // Registriamo comunque lo scroller attraversato, per sapere su cosa agire
  // quando si tocca il tasto della sezione per tornare in cima.
  const onPanelScroll = (e: UIEvent<HTMLDivElement>) => {
    scrollerRef.current = e.target as HTMLElement;
  };

  // Cambiando sezione si riparte dall'alto: la chrome deve tornare con noi,
  // altrimenti si apre una schermata nuova gia' compressa senza averla scrollata.
  useEffect(() => {
    // Volutamente incondizionato: portarlo a false quando gia' lo e' non
    // produce un nuovo stato in Redux Toolkit, quindi non innesca render.
    // Leggere `compact` qui lo trascinerebbe nelle dipendenze e ogni
    // compressione da scroll si annullerebbe da sola al render successivo.
    dispatch(setBannerCompact(false));
  }, [active, dispatch]);

  const current = sections.find((s) => s.key === active) ?? sections[0];
  if (!current) return null;
  const Panel = current.Panel;
  const CurrentIcon = current.Icon;

  return (
    // Il gap si annulla da compressi: la griglia collassata ha altezza zero, ma
    // il `gap` attorno a lei continua a occupare spazio — 12px sopra e 12px
    // sotto per un elemento che non c'e' piu'. Era la meta' del vuoto fra la
    // chrome e l'intestazione della sezione.
    <div className={cn('flex min-h-0 flex-1 flex-col', compact ? 'gap-0' : 'gap-3')}>
      {/* Da compressi, il tasto della sezione attiva si trasferisce accanto al
          banner: sparita la griglia, resta l'unica cosa che dice dove sei.
          Tocco = torna in cima, che e' anche il gesto per rivedere la griglia. */}
      <BannerSideSlot>
        <button
          type="button"
          onClick={() => {
            haptics.selection();
            scrollerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="flex items-center gap-2 rounded-2xl bg-brand px-3.5 py-2.5 text-brand-fg shadow-lg shadow-brand/20"
        >
          <CurrentIcon aria-hidden className="h-5 w-5 shrink-0" strokeWidth={2} />
          <span className="truncate text-xs font-bold uppercase tracking-wide">
            {current.label}
          </span>
        </button>
      </BannerSideSlot>

      {/* Section buttons as explicit rows of four, never a scroller (a button
          you can't see doesn't exist). The row that holds the active section
          stays full (icon + label); every OTHER row collapses to label-only —
          reclaiming height in the fixed-viewport Hub. One row (a non-CR game)
          always holds the active section, so it never collapses. */}
      {/* Le due righe vivono DENTRO un unico pannello, non come otto pastiglie
          sciolte sullo sfondo. Il pannello dice "questa e' la navigazione", e
          solo la voce attiva si stacca come pastiglia piena: cosi' l'occhio
          trova subito dove si trova invece di leggere otto cornici uguali. */}
      {/* Scorrendo la griglia si ritrae; tornando in cima ricompare.
          `max-height` invece di `display:none` perche' e' animabile: la griglia
          scivola via invece di sparire di scatto, e il contenuto sotto sale
          seguendola. `pointer-events-none` da chiusa evita che un tocco finisca
          su un bottone alto zero ma ancora presente. */}
      <div
        ref={gridRef}
        role="tablist"
        data-tour="hub-sections"
        aria-hidden={compact}
        className={cn(
          'relative',
          // Vetro: fondo semitrasparente + sfocatura, cosi' la texture a
          // scaglie traspare invece di essere coperta. Il velo e' `bg-bg/55`
          // (teal) e NON `bg-black/25`: il nero traslucido sopra il verde acqua
          // lo desatura e produce il grigio-verde su cui la stroke leggeva
          // grigia. Un velo dello stesso colore scurisce senza spegnere la
          // tinta, quindi si ottiene il vetro senza riportare il grigio.
          'card-edge flex shrink-0 flex-col gap-1 overflow-hidden rounded-2xl bg-bg/55 backdrop-blur-md',
          'transition-all duration-300 ease-out',
          compact ? 'pointer-events-none max-h-0 p-0 opacity-0' : 'max-h-48 p-1.5 opacity-100',
        )}
        onTouchStart={onTouchStart}
        onTouchEnd={onButtonsTouchEnd}
      >
        {/* La pastiglia attiva è UN elemento che si sposta, su due assi: da
            «Ranked» a «Eventi» scorre di lato, da «Community» a «Classifica»
            scende di riga. Un fondo che si accendesse sul bottone nuovo direbbe
            solo dove sei ARRIVATO; questo dice anche da dove vieni, che su una
            griglia di otto voci è metà dell'informazione. */}
        {gridRect && !compact && (
          <span
            aria-hidden
            className="absolute rounded-xl bg-brand shadow-lg shadow-brand/20"
            style={{
              left: gridRect.left,
              top: gridRect.top,
              width: gridRect.width,
              height: gridRect.height,
              transition: gridReady ? `all 340ms ${BOUNCE}` : undefined,
            }}
          />
        )}

        {chunk(sections, COLS).map((row, rowIndex) => {
          return (
            <div key={rowIndex} className="grid grid-cols-4 gap-1">
              {row.map((s) => {
                const on = s.key === active;
                return (
                  <button
                    key={s.key}
                    data-tour={`hub-section-${s.key}`}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    data-active={on}
                    onClick={() => {
                      // Swallow the click a swipe-across-buttons synthesises.
                      if (swipeConsumedRef.current) {
                        swipeConsumedRef.current = false;
                        return;
                      }
                      selectSection(s.key);
                    }}
                    className={cn(
                      // `relative` porta icona ed etichetta SOPRA la pastiglia
                      // che scorre; il fondo non sta piu' sul bottone.
                      'relative flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 py-2',
                      'transition-colors active:opacity-80',
                      on ? 'text-brand-fg' : 'text-fg-muted',
                    )}
                  >
                    <s.Icon aria-hidden className="h-5 w-5 shrink-0" strokeWidth={2} />
                    <span className="w-full truncate text-center text-[10px] font-bold uppercase tracking-wide">
                      {s.label}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* The only scroller here. overscroll-contain keeps a fling at the end of
          the panel from dragging the Hub around.
          touch-action: pan-y hands vertical scrolling to the browser and leaves
          the horizontal axis to us — without it the swipe fights the scroll and
          both feel broken. */}
      <div
        onScrollCapture={onPanelScroll}
        className={cn(
          'min-h-0 flex-1 [touch-action:pan-y]',
          current.ownScroll ? 'overflow-hidden' : 'overflow-y-auto overscroll-contain',
        )}
        onTouchStart={onTouchStart}
        onTouchEnd={onPanelTouchEnd}
      >
        <Panel />
      </div>
    </div>
  );
}
